import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';

/**
 * Autocomplete input component with suggestions dropdown
 * Supports both single selection and multi-value (comma-separated) modes
 */
function AutocompleteInput({
  id,
  name,
  value,
  onChange,
  suggestions = [],
  placeholder = '',
  label,
  multiValue = false, // If true, supports comma-separated values (like attendees)
  className = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Calculate dropdown position to avoid viewport cutoff
  useEffect(() => {
    if (!isOpen || !dropdownRef.current || !inputRef.current) return;

    const calculatePosition = () => {
      const inputRect = inputRef.current.getBoundingClientRect();
      const dropdownHeight = Math.min(dropdownRef.current.offsetHeight, 240); // max-h-60 = 240px
      const spaceBelow = window.innerHeight - inputRect.bottom;
      const spaceAbove = inputRect.top;

      // If dropdown would go off-screen below and there's more space above, position above
      if (spaceBelow < dropdownHeight + 10 && spaceAbove > spaceBelow) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    };

    // Slight delay to ensure dropdown is rendered
    const timer = setTimeout(calculatePosition, 0);
    window.addEventListener('scroll', calculatePosition);
    window.addEventListener('resize', calculatePosition);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', calculatePosition);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [isOpen]);

  // Get the current "active" part of the input for multi-value mode
  const getCurrentToken = () => {
    if (!multiValue) return value;
    const parts = value.split(',');
    return parts[parts.length - 1].trim();
  };

  // Filter suggestions based on current input
  useEffect(() => {
    const currentToken = getCurrentToken();
    if (!currentToken) {
      setFilteredSuggestions(suggestions);
    } else {
      const filtered = suggestions.filter(s =>
        s.toLowerCase().includes(currentToken.toLowerCase()) &&
        s.toLowerCase() !== currentToken.toLowerCase()
      );
      setFilteredSuggestions(filtered);
    }
    setHighlightedIndex(-1);
  }, [value, suggestions, multiValue]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange({ target: { name, value: newValue } });
    setIsOpen(true);
  };

  const handleSelectSuggestion = (suggestion) => {
    if (multiValue) {
      // Replace the current token with the suggestion
      const parts = value.split(',').map(p => p.trim()).filter(p => p);
      if (parts.length > 0) {
        parts[parts.length - 1] = suggestion;
      } else {
        parts.push(suggestion);
      }
      onChange({ target: { name, value: parts.join(', ') } });
    } else {
      onChange({ target: { name, value: suggestion } });
    }
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredSuggestions[highlightedIndex]) {
          handleSelectSuggestion(filteredSuggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const clearInput = () => {
    onChange({ target: { name, value: '' } });
    inputRef.current?.focus();
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div className="relative mt-1">
        <input
          ref={inputRef}
          type="text"
          id={id}
          name={name}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          autoComplete="off"
          className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <div className="absolute inset-y-0 right-0 flex items-center">
          {value && (
            <button
              type="button"
              onClick={clearInput}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && filteredSuggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className={`absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${
            dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full'
          }`}
        >
          {filteredSuggestions.map((suggestion, index) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className={`w-full px-4 py-2 text-left text-sm ${
                index === highlightedIndex
                  ? 'bg-blue-100 text-blue-900'
                  : 'text-gray-900 hover:bg-gray-100'
              }`}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}

      {/* Show "no suggestions" hint when dropdown open but empty */}
      {isOpen && filteredSuggestions.length === 0 && suggestions.length > 0 && value && (
        <div
          ref={dropdownRef}
          className={`absolute z-20 mt-1 w-full rounded-md bg-white py-2 px-4 shadow-lg ring-1 ring-black ring-opacity-5 ${
            dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full'
          }`}
        >
          <p className="text-sm text-gray-500 italic">No suggestions</p>
        </div>
      )}
    </div>
  );
}

export default AutocompleteInput;
